import React, { createContext, Dispatch, FC, SetStateAction, useCallback, useContext, useRef, useState } from "react";

import { UserContext } from "@/components/User";
import { useWizard } from "@/components/wizards/useWizard";
import { ConsultantProfile } from "@/data/consultant-profiles/types/ConsultantProfile";
import { saveDraftJobApplication, updateDraftJobApplication } from "@/data/job-applications";
import { JobApplication } from "@/data/job-applications/types/JobApplication";
import { JobRequest } from "@/data/job-requests/types/JobRequest";
import { noop, throwError } from "@/utils/functions";

export type TransformDraftFunction = (draft: JobApplication) => JobApplication;

export type SavingDraftState = "SAVING" | "SAVED_RECENTLY" | "SAVED" | "ERROR";

export interface ApplyWizardContextValue {
  jobRequest: JobRequest;
  profile?: ConsultantProfile;
  initialProfile?: ConsultantProfile;
  setProfile: (profile: ConsultantProfile | undefined) => void;
  initialDraft?: JobApplication;
  designatedCandidate?: string;
  draft?: JobApplication;
  setDraft: Dispatch<SetStateAction<JobApplication | undefined>>;
  createOrUpdateDraft: (transformDraft?: TransformDraftFunction) => Promise<JobApplication>;
  savingState?: SavingDraftState;
  resetSavingState: () => void;
  savedDate?: Date;
}

export const ApplyWizardContext = createContext<ApplyWizardContextValue>({
  jobRequest: {} as JobRequest,
  setProfile: noop,
  setDraft: noop,
  createOrUpdateDraft: throwError,
  resetSavingState: noop,
});

export const useApplyWizard = () => {
  const wizardContext = useWizard();
  const applyWizardContext = useContext(ApplyWizardContext);

  return {
    ...wizardContext,
    ...applyWizardContext,
  };
};

export interface ApplyWizardProviderProps {
  jobRequest: JobRequest;
  initialDraft?: JobApplication;
  designatedCandidate?: string;
  initialProfile?: ConsultantProfile;
}

export const ApplyWizardProvider: FC<ApplyWizardProviderProps> = ({
  jobRequest,
  initialDraft,
  designatedCandidate,
  initialProfile,
  children,
}) => {
  const { contextCompany, contextConsultantProfile, hasCompanyManagerPrivileges } = useContext(UserContext);

  const [profile, setProfile] = useState<ConsultantProfile | undefined>(
    initialDraft
      ? initialDraft.consultantProfile
      : !hasCompanyManagerPrivileges()
      ? contextConsultantProfile
      : initialProfile
      ? initialProfile
      : undefined,
  );
  const [draft, setDraft] = useState<JobApplication | undefined>(initialDraft);

  const [savingState, setSavingState] = useState<SavingDraftState>();
  const [savedDate, setSavedDate] = useState<Date>();
  const savingTimeoutId = useRef<NodeJS.Timeout>();

  const createDraft = useCallback(
    async (transformDraft: TransformDraftFunction = draft => draft) => {
      const result = await saveDraftJobApplication(
        {
          jobRequestId: jobRequest.id,
          companyId: contextCompany?.id,
        },
        transformDraft({
          consultantProfile: {
            ...profile,
            cvs: [],
          },
          rate: {
            currency: jobRequest.rate.currency,
          },
          ...draft,
        } as JobApplication),
      );

      setDraft(result);

      return result;
    },
    [jobRequest, profile, draft, contextCompany],
  );

  const updateDraft = useCallback(
    async (transformDraft: TransformDraftFunction = draft => draft) => {
      if (!draft) {
        throw new Error();
      }

      const result = await updateDraftJobApplication(
        {
          jobApplicationId: draft.id,
          companyId: contextCompany?.id,
        },
        transformDraft(draft),
      );

      setDraft(result);

      return result;
    },
    [draft, contextCompany],
  );

  const createOrUpdateDraft = useCallback(
    async (transformDraft?: TransformDraftFunction) => {
      try {
        if (savingTimeoutId.current !== undefined) {
          clearTimeout(savingTimeoutId.current);
          savingTimeoutId.current = undefined;
        }

        setSavingState("SAVING");

        let result: JobApplication;

        if (draft?.id) {
          result = await updateDraft(transformDraft);
        } else {
          result = await createDraft(transformDraft);
        }

        setSavingState("SAVED_RECENTLY");
        setSavedDate(new Date());
        savingTimeoutId.current = setTimeout(() => {
          setSavingState("SAVED");
        }, 2000);

        return result;
      } catch (error) {
        setSavingState("ERROR");

        throw error;
      }
    },
    [draft, createDraft, updateDraft],
  );

  const resetSavingState = useCallback(() => {
    if (savingTimeoutId.current) {
      clearTimeout(savingTimeoutId.current);
      savingTimeoutId.current = undefined;
    }
    setSavingState(undefined);
    setSavedDate(undefined);
  }, []);

  const contextValue: ApplyWizardContextValue = {
    jobRequest,
    profile,
    setProfile,
    initialDraft,
    initialProfile,
    designatedCandidate,
    draft,
    setDraft,
    createOrUpdateDraft,
    savingState,
    resetSavingState,
    savedDate,
  };

  return <ApplyWizardContext.Provider value={contextValue}>{children}</ApplyWizardContext.Provider>;
};
